import {
	getConsentParameters,
	getTrackingParameters,
	trackFormSuccess,
} from "../../../../js/utils/helper/analytics.js";
import { doFetch } from "../../../../js/utils/helper/fetch.js";
import { validateF04OrderForm } from "../../../../js/utils/helper/validators.js";
import {
	displayErrors,
	getFormattedConsentFields,
	getFormTokenHeaders,
	scheduleFormTokenRefresh,
	hideAllErrors,
	initBaseForm,
	processPhoneField,
} from "../../../../js/utils/helper/forms";

export class F04OrderForm extends HTMLElement {
	/** @type {AbortController | null} */
	#abortController = null;

	constructor() {
		super();
	}

	disconnectedCallback() {
		this.#abortController?.abort();
	}

	#setSelectedLanguageOption(dataCountryCode, magazineLanguageField) {
		const magazineLanguageOption = magazineLanguageField.querySelector(
			`[data-country-code="${dataCountryCode}"]`,
		);
		if (magazineLanguageOption) {
			magazineLanguageOption.selected = true;
		}
	}

	/**
	 * Automatically sets the magazine language option based on the user's browser language.
	 *
	 * This method analyzes the navigator.language property to determine the most appropriate
	 * magazine language option. It handles special cases for Belgian Dutch (nl-be) and
	 * Portuguese (pt) languages, with the latter being mapped to Spanish (es) with a
	 * help text notification.
	 * @private
	 * @param {HTMLSelectElement} magazineLanguageField - The select element containing magazine language options
	 * @example
	 * // For a user with navigator.language = "en-US"
	 * // Will select the option with data-country-code="en"
	 * @example
	 * // For a user with navigator.language = "nl-BE"
	 * // Will select the option with data-country-code="nl-be"
	 * @example
	 * // For a user with navigator.language = "pt-BR"
	 * // Will select the option with data-country-code="es" and show help text
	 */
	#setMagazineLanguageOption(magazineLanguageField) {
		const helpTextContainer = magazineLanguageField
			.closest(".Form-fieldGroup")
			.querySelector(".Form-helpText");
		const navigatorLanguage = navigator.language;

		helpTextContainer.hidden = true;

		if (navigatorLanguage.toLowerCase() === "nl-be") {
			this.#setSelectedLanguageOption("nl-be", magazineLanguageField);
			return;
		}

		let navigatorLanguageCountryCode = navigatorLanguage
			.split("-")[0]
			.toLowerCase();

		if (navigatorLanguageCountryCode === "pt") {
			navigatorLanguageCountryCode = "en";
			helpTextContainer.hidden = false;
		}

		this.#setSelectedLanguageOption(
			navigatorLanguageCountryCode,
			magazineLanguageField,
		);
	}

	connectedCallback() {
		this.#abortController = new AbortController();
		const magazineLanguageField = this.querySelector("#magazineLanguage");
		if (magazineLanguageField) {
			this.#setMagazineLanguageOption(magazineLanguageField);
		}
		this.#addEventListeners(this.#abortController.signal);
	}

	/**
	 * Handles the form submission event.
	 * Prevents the default form submission, gathers and formats form data,
	 * validates the data, displays errors if validation fails, and sends the data
	 * to the server if validation succeeds.
	 * @param {Event} event - The form submission event.
	 * @returns {Promise<void>}
	 */
	#handleSubmit = async (event) => {
		event.preventDefault();

		const form = this.querySelector("form");
		const successUrl = form.dataset.success;

		const submitButton = form.querySelector("button[type='submit']");
		submitButton.classList.toggle("Button--loading");
		submitButton.disabled = true;

		hideAllErrors(this);

		const formData = new FormData(form);
		let constructedData = Object.fromEntries(formData);

		constructedData = processPhoneField(
			constructedData.phone,
			constructedData,
			/** minAllowedLength */ 5,
		);

		constructedData = {
			...constructedData,
			...getFormattedConsentFields(constructedData),
		};

		const validation = validateF04OrderForm(constructedData);

		if (!validation.success) {
			displayErrors(validation.errors, this);
			submitButton.classList.toggle("Button--loading");
			submitButton.disabled = false;
			return;
		}

		const additionalData = document.querySelector("#additional-form-data");
		if (additionalData) {
			try {
				constructedData.additionalData = JSON.parse(additionalData.textContent);
			} catch (error) {
				throw new Error("Invalid JSON in additional data", { cause: error });
			}
		}

		const serverResponse = await doFetch(constructedData, form.action, {
			headers: getFormTokenHeaders(),
		});

		if (serverResponse && successUrl) {
			trackFormSuccess({
				form: "f04-order-form",
				formDestination: successUrl,
				parameters: {
					...getConsentParameters(form),
					...getTrackingParameters(form, {
						contact_role: "role",
						magazine_language: "magazineLanguage",
					}),
				},
			});
			window.location.assign(successUrl);
		} else {
			submitButton.classList.toggle("Button--loading");
			submitButton.disabled = false;
		}
	};

	/**
	 * Adds event listeners to the form elements for handling country changes and form submission.
	 * @private
	 */
	/**
	 * @param {AbortSignal} signal
	 */
	#addEventListeners(signal) {
		const form = this.querySelector("form");
		const magazineLanguageField = this.querySelector("#magazineLanguage");

		form.setAttribute("novalidate", "novalidate");

		if (magazineLanguageField) {
			magazineLanguageField.addEventListener(
				"change",
				() => {
					const helpTextContainer = magazineLanguageField
						.closest(".Form-fieldGroup")
						.querySelector(".Form-helpText");
					helpTextContainer.hidden = true;
				},
				{ signal },
			);
		}

		initBaseForm(form, {
			hasNationality: false,
			hasPhoneNumber: true,
			hasProvince: true,
		});

		form.addEventListener("focusin", scheduleFormTokenRefresh, {
			once: true,
			signal,
		});
		form.addEventListener("submit", this.#handleSubmit, { signal });
	}
}

customElements.define("finstral-f04-order-form", F04OrderForm);
